import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Send } from "lucide-react";
import { Button } from "@/components/ui/button";

const SUGGESTED = [
  "Draft a proposal for the Shopify Plus migration job",
  "Which RFPs match our federal certifications?",
  "Summarize today's high-value LinkedIn prospects",
  "Write a follow-up email for the Belvedere Wines lead",
];

const INITIAL_MESSAGES = [
  {
    role: "assistant",
    content:
      "Hi — I'm your Base2Brand AI copilot. I can draft proposals, score leads, summarize RFPs, or launch outreach workflows. What would you like to work on?",
  },
];

export default function AICopilot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(INITIAL_MESSAGES);
  const [input, setInput] = useState("");

  const send = (text) => {
    const value = (text ?? input).trim();
    if (!value) return;
    setMessages((m) => [
      ...m,
      { role: "user", content: value },
      {
        role: "assistant",
        content:
          "Working on it… (this is a demo — connect an LLM provider in Settings to get live responses).",
      },
    ]);
    setInput("");
  };

  return (
    <>
      {/* Floating trigger */}
      <button
        data-testid="ai-copilot-trigger"
        onClick={() => setOpen(true)}
        className="fixed z-40 bottom-6 right-6 group"
        aria-label="Open AI Copilot"
      >
        <span className="absolute inset-0 rounded-full pulse-ring" />
        <span className="relative flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 via-indigo-500 to-cyan-400 glow-indigo transition-transform group-hover:scale-105">
          <Sparkles className="h-5 w-5 text-white" strokeWidth={2.5} />
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
              onClick={() => setOpen(false)}
            />
            <motion.div
              data-testid="ai-copilot-panel"
              initial={{ opacity: 0, y: 20, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.98 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="fixed bottom-6 right-6 z-50 w-[92vw] max-w-[420px] h-[70vh] max-h-[640px] flex flex-col rounded-2xl border border-white/[0.08] bg-[#0d0d10]/95 backdrop-blur-2xl shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
                <div className="flex items-center gap-2.5">
                  <span className="h-8 w-8 rounded-lg bg-gradient-to-br from-indigo-500 to-cyan-400 grid place-items-center">
                    <Sparkles className="h-3.5 w-3.5 text-white" />
                  </span>
                  <div className="leading-tight">
                    <div className="font-display text-[14px] font-semibold">AI Copilot</div>
                    <div className="font-mono text-[10px] uppercase tracking-wider text-white/50">
                      claude sonnet · online
                    </div>
                  </div>
                </div>
                <Button
                  data-testid="ai-copilot-close"
                  variant="ghost"
                  size="icon"
                  onClick={() => setOpen(false)}
                  className="text-white/50 hover:text-white hover:bg-white/[0.05]"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
                {messages.map((m, i) => (
                  <div
                    key={i}
                    className={
                      m.role === "user"
                        ? "flex justify-end"
                        : "flex justify-start"
                    }
                  >
                    <div
                      className={
                        m.role === "user"
                          ? "max-w-[80%] rounded-2xl rounded-br-sm bg-indigo-500/90 px-4 py-2.5 text-[13.5px]"
                          : "max-w-[85%] rounded-2xl rounded-bl-sm bg-white/[0.05] border border-white/[0.06] px-4 py-2.5 text-[13.5px] text-white/85"
                      }
                    >
                      {m.content}
                    </div>
                  </div>
                ))}
              </div>

              <div className="px-5 pb-3">
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {SUGGESTED.map((s) => (
                    <button
                      key={s}
                      data-testid={`ai-suggested-${s.slice(0, 12).replace(/\s+/g, "-")}`}
                      onClick={() => send(s)}
                      className="text-[11px] rounded-full border border-white/[0.08] bg-white/[0.02] px-2.5 py-1 text-white/60 hover:text-white hover:border-white/[0.16] transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    send();
                  }}
                  className="flex items-center gap-2 rounded-xl border border-white/[0.08] bg-white/[0.02] px-3 py-2 focus-within:border-indigo-400/50"
                >
                  <input
                    data-testid="ai-copilot-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Ask, plan, or draft anything…"
                    className="flex-1 bg-transparent text-[13.5px] text-white placeholder:text-white/40 outline-none"
                  />
                  <Button
                    data-testid="ai-copilot-send"
                    type="submit"
                    size="icon"
                    className="rounded-lg bg-indigo-500 hover:bg-indigo-400 h-7 w-7"
                  >
                    <Send className="h-3.5 w-3.5" />
                  </Button>
                </form>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
